// axiosInstance.ts
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { getConfig } from '../../../shared/config/AppConfig';
import { authStorage } from '../../../features/auth/api/authStorage';
import { userIdStorage } from '../../../features/auth/api/userIdStorage';
import { agencyStorage } from '../../../features/auth/api/agencyStorage';
import { timeZoneStorage } from '../../../features/auth/api/timeZoneStorage';
import store from '../../../shared/store';
import { setIsNetworkError, showErrorModal, showSessionExpiredMessage } from '../../../shared/store/errorSlice';
import { refreshAccessToken } from '../services/authProvider';
import { SESSION_EXPIRED_MSG } from '../constants/authConstants';

let onSessionExpired: (() => void) | null = null;

export const setSessionExpiredCallback = (cb: () => void) => {
  onSessionExpired = cb;
};

// Manages the promise of a failed network request, allowing it to be retried.
export const networkErrorManager = {
  requestQueue: [] as {
    request: InternalAxiosRequestConfig;
    promise: {
      resolve: (value: any) => void;
      reject: (reason?: any) => void;
    };
  }[],

  storeRequestAndPromise(
    req: InternalAxiosRequestConfig,
    res: (value: any) => void,
    rej: (reason?: any) => void,
  ) {
    this.requestQueue.push({
      request: req,
      promise: { resolve: res, reject: rej },
    });
  },

  retryAllRequests() {
    const promises = this.requestQueue.map(({ request, promise }) =>
      api(request).then(promise.resolve).catch(promise.reject),
    );
    this.clear();
    return Promise.all(promises);
  },

  rejectAllRequests(reason: any) {
    this.requestQueue.forEach(({ promise }) => {
      promise.reject(reason);
    });
    this.clear();
  },

  clear() {
    this.requestQueue = [];
  },
};

// Tracks transient-retry counts per request, keyed by "<method>:<url>".
// Using a stable string key avoids the WeakMap pitfall where Axios creates
// a new config object for each outgoing attempt, making the previous WeakMap
// key unreachable. Entries are removed once retries are exhausted or succeed.
const transientRetryMap = new Map<string, number>();

// Flag to prevent concurrent refresh attempts triggering multiple logouts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

const onTokenRefreshed = (token: string | null) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const api: AxiosInstance = axios.create({
  baseURL: (getConfig()?.API_BASE_URL ?? '').replace(/\/+$/, ''),
  timeout: 30000,
  withCredentials: true, // Required for TTAuth cookie session on web; harmless for Entra
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/* ---------------- REQUEST INTERCEPTOR ---------------- */
api.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    const token = await authStorage.getAccessToken();

    // Never send the TTAuth cookie-only sentinel as a Bearer token.
    // When /cookie/me returns no body token, ttAuthAdapter stores the sentinel
    // 'ttauth-cookie-session' as a navigation trigger only. BFF authentication
    // for that flow relies on the HttpOnly cookie (withCredentials:true).
    // When /cookie/me returns a real JWT, it is stored directly and sent here.
    const isTTAuthSentinel = token === 'ttauth-cookie-session';
    if (token && !isTTAuthSentinel) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    const userId = userIdStorage.get();
    if (userId !== null) {
      config.headers.set('X-UserId', String(userId));
    }

    // X-Agency-Id carries the agency name the user entered (e.g. "Transit-Local").
    // The BFF uses this to identify the tenant; no separate agencyCode is returned
    // by the validate endpoint — the name itself is the identifier.
    const agencyId = agencyStorage.get();
    if (agencyId !== null) {
      config.headers.set('X-Agency-Id', agencyId);
    }

    const timeZoneId = timeZoneStorage.get();
    if (timeZoneId !== null) {
      config.headers.set('X-TimeZone', timeZoneId);
    }

    return config;
  },
  error => Promise.reject(error),
);

/* ---------------- RESPONSE INTERCEPTOR ---------------- */
api.interceptors.response.use(
  response => {
    // Detect session-expired message returned inside a successful (2xx) response body.
    if (response?.data?.message?.includes(SESSION_EXPIRED_MSG)) {
      store.dispatch(showSessionExpiredMessage());
    }
    return response;
  },
  async (error: AxiosError<any>) => {
    // Handle network errors (no response from server)
    if (!error.response) {
      // Axios timeout errors have no response but should NOT show the network
      // error modal — they are expected when the API is slow or unreachable for
      // a single request and should be handled by the caller.
      if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
        return Promise.reject(error);
      }

      // On Android, CustomTabsIntent suspends the OS socket layer while the
      // OIDC browser tab is open.  When the tab closes, both TCP and NetInfo
      // report offline for ~1–2 s while the socket layer reinitialises.
      // Any API call fired during this window (e.g. dashboard data fetch on
      // DashboardScreen mount) receives no response.  Rather than showing the
      // network error modal straight away, retry up to 3 times with exponential
      // back-off (500 ms → 1 s → 2 s) to give the socket layer time to recover.
      // Only if all retries fail do we fall through to the modal path.
      // A stable "<method>:<url>" key is used so the count survives across
      // Axios config object re-creations on each retry attempt.
      if (error.config) {
        const retryKey = `${error.config.method ?? 'get'}:${error.config.url ?? ''}`;
        const attempt = transientRetryMap.get(retryKey) ?? 0;
        const DELAYS = [500, 1000, 2000];
        if (attempt < DELAYS.length) {
          transientRetryMap.set(retryKey, attempt + 1);
          const cfg = error.config;
          return new Promise<any>((resolve, reject) => {
            setTimeout(() => {
              api(cfg)
                .then(res => {
                  transientRetryMap.delete(retryKey);
                  resolve(res);
                })
                .catch(retryErr => {
                  // If this was the last retry attempt, clean up the map so
                  // subsequent focus cycles (e.g. user navigates away and back)
                  // get a fresh retry budget rather than 0 remaining.
                  if ((transientRetryMap.get(retryKey) ?? 0) >= DELAYS.length) {
                    transientRetryMap.delete(retryKey);
                  }
                  reject(retryErr);
                });
            }, DELAYS[attempt]);
          });
        }
        // All retries exhausted — clean up and fall through to modal.
        transientRetryMap.delete(retryKey);
      }

      return new Promise((resolve, reject) => {
        networkErrorManager.storeRequestAndPromise(
          error.config as InternalAxiosRequestConfig,
          resolve,
          reject,
        );
        store.dispatch(setIsNetworkError({ isNetworkError: true }))
        store.dispatch(
          showErrorModal({
            title: 'common.networkError.title',
            subTitle: 'common.networkError.subTitle',
            message: 'common.networkError.desc',
            ctas: [
              { label: 'common.networkError.tryAgaincta', type: 'RETRY' },
            ],
            screen: '',
            noTranslation: false
          }),
        );
      });
    }

    store.dispatch(setIsNetworkError({ isNetworkError: false }));

    // Handle session expired (401) — attempt silent token refresh first.
    //
    // Special case — verify-pin endpoint:
    // This endpoint returns 401 for TWO different reasons:
    //   (a) Wrong PIN      — session is alive, PIN digits are incorrect
    //   (b) Expired session — TTAuth cookie expired, BFF rejects the request
    //
    // We cannot tell them apart from the status code alone.
    // Strategy: silently refresh the session first, then retry verify-pin.
    //   • If the retry also returns 401 → session is alive, PIN was wrong
    //     → reject with the retry's 401 so the caller shows "incorrect PIN".
    //   • If the retry succeeds → session was expired but PIN was correct ✓
    //   • If refresh itself returns null → session is truly expired → onSessionExpired()
    //   • If refresh throws a network error → transient, reject without logout
    //
    // /api/auth/validate returning 401 is non-fatal — it means the BFF does not
    // yet accept the current token type (e.g. TTAuth JWTs pending backend update).
    // The caller (validateRole in ShopHomePage) has its own catch block that keeps
    // the user on ShopHomePage. Triggering onSessionExpired here would cause an
    // immediate logout, which is wrong — so we still skip it entirely.
    //
    // Note: TTAuth cookie-only sessions (sentinel stored) ARE handled here.
    // When the TTAuth session cookie expires, the BFF returns 401 and we must
    // attempt a silent refresh via /ttauth/v1/cookie/me — same as any other
    // token. refreshAccessToken() (ttAuthAdapter.web.ts) will return either:
    //   • a real JWT  → store as Bearer token and retry
    //   • the sentinel 'ttauth-cookie-session' → TTAuth refreshed its cookie
    //     proactively; retry the original request without a Bearer header
    //     (withCredentials:true sends the new cookie automatically)
    //   • null → the session is truly expired → call onSessionExpired()
    const isPinVerifyRequest = error.config?.url?.includes('verify-pin') ?? false;
    const isValidateRequest = error.config?.url?.includes('auth/validate') ?? false;

    if (error.response.status === 401 && !isValidateRequest) {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          // Another refresh is already in progress — queue this request.
          // For verify-pin: once the queued retry fires and returns 401 again,
          // that 401 bubbles up to the caller as "wrong PIN" (correct behaviour).
          return new Promise((resolve, reject) => {
            refreshSubscribers.push((token: string | null) => {
              if (token) {
                const isSentinel = token === 'ttauth-cookie-session';
                if (!isSentinel) {
                  originalRequest.headers.set('Authorization', `Bearer ${token}`);
                }
                resolve(api(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        isRefreshing = true;
        try {
          console.log(`[axiosInstance] 401 received${isPinVerifyRequest ? ' (verify-pin — refreshing session before retry)' : ''} — attempting token refresh`);
          const newToken = await refreshAccessToken();
          isRefreshing = false;

          if (newToken) {
            const isSentinel = newToken === 'ttauth-cookie-session';
            if (isSentinel) {
              // TTAuth cookie-only: session cookie was refreshed by /cookie/me.
              // Remove any stale Authorization header; the new cookie is sent
              // automatically via withCredentials:true.
              console.log('[axiosInstance] TTAuth cookie session refreshed — retrying original request via cookie');
              originalRequest.headers.delete('Authorization');
            } else {
              console.log('[axiosInstance] Token refreshed — retrying original request with new Bearer token');
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
            }
            onTokenRefreshed(newToken);
            // For verify-pin: if the retry returns 401, that 401 propagates to
            // the caller as "wrong PIN" (session was alive, digits were wrong).
            return api(originalRequest);
          } else {
            console.warn('[axiosInstance] Token refresh returned null — session expired');
            onTokenRefreshed(null);
            await authStorage.clear();
            onSessionExpired?.();
          }
        } catch (refreshError) {
          isRefreshing = false;
          // Distinguish between a network/fetch error (transient) and a
          // confirmed session expiry (refresh token rejected by server).
          //
          // ttAuthService.web.ts / ttAuthService.native.ts now propagate
          // fetch/network errors as thrown exceptions rather than returning
          // null, so we can detect them here and avoid signing the user out
          // on a transient connectivity blip.
          //
          // A network error looks like a TypeError ("Failed to fetch",
          // "Network request failed") or an Axios error with no response.
          // A confirmed expiry is a plain null return (caught above) or a
          // non-network throw.
          const isNetworkError =
            refreshError instanceof TypeError ||
            (refreshError instanceof Error &&
              (refreshError.message?.toLowerCase().includes('network') ||
               refreshError.message?.toLowerCase().includes('fetch') ||
               refreshError.message?.toLowerCase().includes('failed to fetch') ||
               refreshError.message?.toLowerCase().includes('network request failed')));

          if (isNetworkError) {
            // Transient: don't sign the user out — just surface the error
            // so the caller knows the request failed. The user's session is
            // still valid; the next API call (when connectivity is restored)
            // will succeed normally.
            console.warn('[axiosInstance] Token refresh failed due to network error — not signing out:', refreshError);
            onTokenRefreshed(null);
            // Reject without clearing auth or calling onSessionExpired so the
            // user stays logged in.
            return Promise.reject(error);
          }

          // Non-transient: refresh token is genuinely invalid → full sign-out.
          console.warn('[axiosInstance] Token refresh threw a non-network error — session expired:', refreshError);
          onTokenRefreshed(null);
          await authStorage.clear();
          onSessionExpired?.();
        }
      } else {
        // Already retried once and still got 401.
        // For verify-pin: this means session was refreshed but PIN was wrong →
        //   reject so the caller shows "incorrect PIN" (do NOT sign out).
        // For all other endpoints: session is genuinely expired → sign out.
        if (isPinVerifyRequest) {
          console.warn('[axiosInstance] verify-pin returned 401 after session refresh — wrong PIN, not signing out');
          // Fall through to the bottom reject so the caller gets { status: 401 }.
        } else {
          await authStorage.clear();
          onSessionExpired?.();
        }
      }
    }

    if (error.response.status === 404 || error.response.status === 403) {
      return error.response;
    }

    const responseMessage =
      error.response.data?.message || error.message || 'Something went wrong';

    // Detect session-expired message returned inside an error response body.
    if (responseMessage?.includes(SESSION_EXPIRED_MSG)) {
      store.dispatch(showSessionExpiredMessage());
    }

    return Promise.reject({
      status: error.response.status,
      message: responseMessage,
      data: error.response.data,
    });
  },
);

export default api;
